import { useMemo } from "react";
import { _ } from "i18n";
import { toNullableInt } from "libs/utils";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { PRIORITY_SELECT_ITEMS } from "../constants";
import { isDiscoverGamesGridCellEmpty, isPriorityEditableForStatus } from "../formatters";
import type { GameRow } from "../types";
import { discoverGameRowId } from "../rowHelpers";

/** Per-game priority (`GameMetadata.priority` on PATCH; UI falls back to default `5` when unset). */
export function DiscoverGamesPriorityDropdownCell({
	row,
	isUpdating = false,
	onChange,
	readOnlyTitle,
	canEditByPermission = false,
}: {
	row: GameRow;
	isUpdating?: boolean;
	onChange?: (gameId: number, priority: number) => void;
	readOnlyTitle?: string;
	canEditByPermission?: boolean;
}) {
	const gameId = discoverGameRowId(row);
	const raw = row.priority;
	const hasPriorityValue = !isDiscoverGamesGridCellEmpty(raw);
	const displayPriority = hasPriorityValue ? String(raw) : "5";
	const statusAllowsPriorityEdit = useMemo(
		() => isPriorityEditableForStatus(row.gameAnalysisStatus),
		[row.gameAnalysisStatus],
	);
	const canEditPriority = useMemo(
		() => canEditByPermission === true && statusAllowsPriorityEdit,
		[canEditByPermission, statusAllowsPriorityEdit],
	);
	const selectedOption = useMemo(
		() => PRIORITY_SELECT_ITEMS.find((item) => item.value === displayPriority) ?? null,
		[displayPriority],
	);

	if (!onChange || gameId === null) {
		return (
			<div className="thinCell" title={readOnlyTitle}>
				{displayPriority}
			</div>
		);
	}

	return (
		<div className="thinCell">
			<SelectComponent
				placeholder={_("discover.gamesTable.cellValue.none")}
				value={selectedOption}
				items={PRIORITY_SELECT_ITEMS}
				isDisabled={isUpdating || !canEditPriority}
				menuPortalTarget
				handleChange={(option) => {
					if (option?.value === undefined || option.value === null || option.value === "") {
						return;
					}
					const next = toNullableInt(option.value);
					if (next === null) {
						return;
					}
					onChange(gameId, next);
				}}
			/>
		</div>
	);
}
